import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BBALoader from '../../components/BBALoader';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import BackButton from '../../components/BackButton';
import CrmStatCard from '../../components/crm/CrmStatCard';
import { useAuth } from '../../contexts/AuthContext';
import { getUserData, CRM_OWNER_EXECUTIVE_ROLES } from '../../services/accessControlService';
import { deleteBid, fetchBidById } from '../../services/alaCarteBidService';
import { getAccountById, getCrmCompanyLogoDisplayUrl } from '../../services/firestoreService';
import { computePricingMetrics } from '../../utils/alaCarteCatalog';
import {
  PRICING_STATUS,
  BID_STATUS_LABELS,
  BID_CONTRACT_STATUS,
  BID_CONTRACT_STATUS_LABELS,
  CLIENT_RESPONSE_STATUS,
  CLIENT_RESPONSE_LABELS,
  accountOverviewPath,
  departmentToPostedTeam,
  teamQueuePathForBid,
  INTERNAL_BID_COMPANY,
  bidAccountTypeLabel,
  isInternalBid,
} from '../../utils/alaCarteBid';
import {
  Building2,
  Check,
  DollarSign,
  ExternalLink,
  FileSignature,
  Mail,
  Percent,
  Phone,
  Send,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react';
import PortalBackground from '../../components/PortalBackground';

const CARD = 'rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900';
const FIELD =
  'w-full rounded-xl border border-gray-200 bg-white py-2.5 text-sm text-gray-900 shadow-sm placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-red-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-800/50';
const LABEL = 'mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400';

function parseMoney(raw) {
  if (raw === '' || raw === undefined || raw === null) return NaN;
  const n = parseFloat(String(raw).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

function formatMoney(n) {
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? new Date(parsed) : null;
}

function formatDate(value) {
  const d = toDate(value);
  return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

function formatDateTime(value) {
  const d = toDate(value);
  return d
    ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';
}

function statusPillClass(status) {
  switch (status) {
    case PRICING_STATUS.published:
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800';
    case PRICING_STATUS.awaiting_team:
      return 'bg-amber-100 text-amber-900 border-amber-200 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-800';
    case PRICING_STATUS.team_submitted:
    case PRICING_STATUS.exec_ready:
      return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700';
  }
}

function contactDisplayName(account) {
  const c = account?.contact || {};
  const full = `${c.firstName || ''} ${c.lastName || ''}`.trim();
  return full || c.name || '';
}

function internalUserName(data) {
  if (!data) return '';
  const full = `${data.firstName || ''} ${data.lastName || ''}`.trim();
  return data.name || data.displayName || full || data.email || '';
}

/**
 * Where the bid sits in its lifecycle: one entry per stage with a state of
 * done / current / upcoming / skipped / failed and an optional date caption.
 */
function buildStages(bid) {
  const status = bid.pricingStatus;
  const direct = bid.pricedDirectlyByExec === true;
  const response = bid.clientResponse?.status || '';
  const contractStatus = bid.contract?.status || '';
  const order = [
    PRICING_STATUS.draft,
    PRICING_STATUS.awaiting_team,
    PRICING_STATUS.team_submitted,
    PRICING_STATUS.exec_ready,
    PRICING_STATUS.published,
  ];
  const reached = (s) => order.indexOf(status) >= order.indexOf(s);
  const published = status === PRICING_STATUS.published;

  const stages = [
    { key: 'created', label: 'Created', state: 'done', caption: formatDate(bid.createdAt) },
    {
      key: 'team',
      label: 'With team',
      state: direct ? 'skipped' : status === PRICING_STATUS.awaiting_team ? 'current' : reached(PRICING_STATUS.awaiting_team) || bid.postedAt ? 'done' : status === PRICING_STATUS.draft ? 'current' : 'upcoming',
      caption: direct ? 'Priced by exec' : formatDate(bid.postedAt) || (status === PRICING_STATUS.draft ? 'Draft — not sent' : ''),
    },
    {
      key: 'priced',
      label: 'Team priced',
      state: direct
        ? 'skipped'
        : bid.teamSubmittedAt || status === PRICING_STATUS.team_submitted
          ? 'done'
          : reached(PRICING_STATUS.exec_ready)
            ? 'skipped'
            : 'upcoming',
      caption:
        direct || (!bid.teamSubmittedAt && reached(PRICING_STATUS.exec_ready))
          ? 'Skipped'
          : formatDate(bid.teamSubmittedAt),
    },
    {
      key: 'review',
      label: 'Exec review',
      state: published ? 'done' : status === PRICING_STATUS.team_submitted || status === PRICING_STATUS.exec_ready ? 'current' : 'upcoming',
      caption:
        status === PRICING_STATUS.team_submitted
          ? 'Needs client price'
          : status === PRICING_STATUS.exec_ready
            ? 'Ready to publish'
            : '',
    },
    {
      key: 'published',
      label: 'Published',
      state: published ? 'done' : 'upcoming',
      caption: formatDate(bid.publishedAt),
    },
    {
      key: 'client',
      label: 'Client decision',
      state: !published
        ? 'upcoming'
        : response === CLIENT_RESPONSE_STATUS.accepted
          ? 'done'
          : response === CLIENT_RESPONSE_STATUS.declined
            ? 'failed'
            : 'current',
      caption: response
        ? `${CLIENT_RESPONSE_LABELS[response]}${
            response === CLIENT_RESPONSE_STATUS.countered && bid.clientResponse.counterPrice
              ? ` · ${formatMoney(parseMoney(bid.clientResponse.counterPrice))}`
              : ''
          }`
        : published
          ? 'Waiting on client'
          : '',
    },
    {
      key: 'contract',
      label: 'Contract signed',
      state:
        contractStatus === BID_CONTRACT_STATUS.completed
          ? 'done'
          : contractStatus === BID_CONTRACT_STATUS.declined || contractStatus === BID_CONTRACT_STATUS.voided
            ? 'failed'
            : contractStatus
              ? 'current'
              : 'upcoming',
      caption: contractStatus ? BID_CONTRACT_STATUS_LABELS[contractStatus] : '',
    },
  ];
  return stages;
}

/**
 * Activity log: milestones derived from the bid's own timestamps (so older bids get a
 * history too) merged with explicit `activityLog` entries written by this page.
 */
function buildActivity(bid, nameFor) {
  const by = (uid) => (uid ? nameFor(uid) : '');
  const events = [];
  const push = (at, title, detail, who, tone = 'gray') => {
    const d = toDate(at);
    if (d) events.push({ at: d, title, detail, who, tone });
  };

  const sourceLabel =
    bid.fromProgramSignup === true ? 'from the client’s program signup' : bid.source === 'portal' ? 'from a portal request' : 'in the CRM';
  push(bid.createdAt, 'Bid created', `${bid.isPackage ? 'Package' : 'Service'} bid ${sourceLabel}`, by(bid.createdByUid));
  if (bid.pricedDirectlyByExec === true) {
    push(bid.createdAt, 'Priced by exec', `Client price ${formatMoney(parseMoney(bid.clientListPrice))} entered at creation`, by(bid.createdByUid), 'blue');
  }
  push(bid.postedAt, 'Sent to team', `${departmentToPostedTeam(bid.department || 'General')} team bid queue`, by(bid.postedByUid), 'amber');
  push(bid.teamSubmittedAt, 'Team submitted pricing', '', '', 'blue');
  push(bid.publishedAt, 'Published to client portal', `Client price ${formatMoney(parseMoney(bid.clientListPrice))}`, by(bid.publishedByUid), 'green');
  const cr = bid.clientResponse || {};
  if (cr.status) {
    const detail =
      cr.status === CLIENT_RESPONSE_STATUS.countered
        ? `Countered at ${formatMoney(parseMoney(cr.counterPrice))}${cr.note ? ` — “${cr.note}”` : ''}`
        : cr.note
          ? `“${cr.note}”`
          : '';
    push(
      cr.respondedAt,
      `Client ${CLIENT_RESPONSE_LABELS[cr.status].toLowerCase()}`,
      detail,
      cr.respondedByName,
      cr.status === CLIENT_RESPONSE_STATUS.declined ? 'red' : 'green'
    );
  }
  const c = bid.contract || {};
  push(c.sentAt, 'Contract sent for signature', c.signerEmail ? `To ${c.signerName || c.signerEmail}` : '', '', 'blue');
  if (c.status === BID_CONTRACT_STATUS.completed) push(c.signedAt, 'Contract signed', c.signerName ? `By ${c.signerName}` : '', '', 'green');

  (Array.isArray(bid.activityLog) ? bid.activityLog : []).forEach((entry) => {
    push(entry?.at, entry?.title || 'Updated', entry?.detail || '', by(entry?.byUid), entry?.tone || 'gray');
  });

  return events.sort((a, b) => b.at - a.at);
}

const STAGE_DOT = {
  done: 'bg-green-500 border-green-500 text-white',
  current: 'bg-white border-red-500 text-red-600 ring-4 ring-red-500/15 dark:bg-gray-900',
  upcoming: 'bg-white border-gray-300 text-gray-400 dark:bg-gray-900 dark:border-gray-700',
  skipped: 'bg-gray-100 border-gray-300 border-dashed text-gray-400 dark:bg-gray-800 dark:border-gray-600',
  failed: 'bg-red-500 border-red-500 text-white',
};

const TIMELINE_DOT = {
  gray: 'bg-gray-400',
  blue: 'bg-blue-500',
  amber: 'bg-amber-500',
  green: 'bg-green-500',
  red: 'bg-red-500',
};

function StageTracker({ stages }) {
  return (
    <ol className="grid grid-cols-2 gap-y-6 sm:grid-cols-4 lg:grid-cols-7">
      {stages.map((stage, i) => (
        <li key={stage.key} className="relative flex flex-col items-center text-center">
          {i > 0 && (
            <span
              aria-hidden
              className={`absolute right-1/2 top-4 hidden h-0.5 w-full -translate-y-1/2 lg:block ${
                stage.state === 'done' || stage.state === 'current' || stage.state === 'failed'
                  ? 'bg-green-400 dark:bg-green-700'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          )}
          <span
            className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold ${STAGE_DOT[stage.state]}`}
          >
            {stage.state === 'done' ? (
              <Check className="h-4 w-4" />
            ) : stage.state === 'failed' ? (
              <X className="h-4 w-4" />
            ) : (
              i + 1
            )}
          </span>
          <span
            className={`mt-2 text-sm font-semibold ${
              stage.state === 'current'
                ? 'text-red-600 dark:text-red-400'
                : stage.state === 'upcoming' || stage.state === 'skipped'
                  ? 'text-gray-400 dark:text-gray-500'
                  : 'text-gray-900 dark:text-white'
            }`}
          >
            {stage.label}
          </span>
          {stage.caption && <span className="mt-0.5 px-1 text-xs text-gray-500 dark:text-gray-400">{stage.caption}</span>}
        </li>
      ))}
    </ol>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-gray-100 py-2.5 last:border-b-0 dark:border-gray-800">
      <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-right text-sm font-medium text-gray-900 dark:text-white">{children}</dd>
    </div>
  );
}

export default function AlaCarteBidOverview() {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bid, setBid] = useState(null);
  const [account, setAccount] = useState(null);
  const [canBid, setCanBid] = useState(false);
  const [isExec, setIsExec] = useState(false);
  const [userNames, setUserNames] = useState({});

  const [costInput, setCostInput] = useState('');
  const [clientPriceInput, setClientPriceInput] = useState('');
  const [scopeInput, setScopeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    if (!bidId) return;
    setError('');
    try {
      const data = await fetchBidById(bidId);
      if (!data) {
        setError('Bid not found');
        setBid(null);
        return;
      }
      setBid(data);

      const userData = await getUserData(currentUser?.uid);
      const exec = !!userData && CRM_OWNER_EXECUTIVE_ROLES.includes(userData.role);
      const team = departmentToPostedTeam(data.department || 'General');
      const teamMatch = !!userData?.team && (userData.team === team || userData.team === data.postedToTeam);
      setIsExec(exec);
      setCanBid(exec || teamMatch);

      setCostInput(data.internalCost !== '' && data.internalCost != null ? String(data.internalCost) : '');
      setClientPriceInput(data.clientListPrice !== '' && data.clientListPrice != null ? String(data.clientListPrice) : '');
      setScopeInput((data.scope || '').trim() || (data.teamInput?.scope || '').trim());
      setConfirmPublish(false);

      if (data.accountId && !isInternalBid(data)) {
        setAccount(await getAccountById(data.accountId).catch(() => null));
      }
    } catch (e) {
      console.error(e);
      setError('Failed to load bid');
      setBid(null);
    } finally {
      setLoading(false);
    }
  }, [bidId, currentUser]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Resolve "who" for the activity log — internal user names by uid.
  useEffect(() => {
    if (!bid) return;
    const uids = new Set(
      [
        bid.createdByUid,
        bid.postedByUid,
        bid.publishedByUid,
        ...(Array.isArray(bid.activityLog) ? bid.activityLog.map((e) => e?.byUid) : []),
      ].filter((uid) => uid && !(uid in userNames))
    );
    if (uids.size === 0) return;
    let cancelled = false;
    Promise.all(
      [...uids].map((uid) =>
        getUserData(uid)
          .then((u) => [uid, internalUserName(u?.data)])
          .catch(() => [uid, ''])
      )
    ).then((pairs) => {
      if (!cancelled) setUserNames((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });
    return () => {
      cancelled = true;
    };
  }, [bid, userNames]);

  const stages = useMemo(() => (bid ? buildStages(bid) : []), [bid]);
  const activity = useMemo(() => (bid ? buildActivity(bid, (uid) => userNames[uid] || '') : []), [bid, userNames]);

  if (loading) {
    return (
      <div className="relative flex min-h-screen items-center justify-center py-24">
        <PortalBackground />
        <div className="relative">
          <BBALoader size={48} />
        </div>
      </div>
    );
  }

  if (error || !bid) {
    return (
      <div className="relative min-h-screen p-4 md:p-6 lg:p-8">
        <PortalBackground />
        <div className="relative">
          <BackButton to="/crm/bids" />
          <p className="mt-6 text-center text-gray-600 dark:text-gray-400">{error || 'Not found'}</p>
        </div>
      </div>
    );
  }

  const published = bid.pricingStatus === PRICING_STATUS.published;
  const pricingLocked = published;
  const savedCost = parseMoney(bid.internalCost);
  const savedClient = parseMoney(bid.clientListPrice);
  const liveCost = parseMoney(costInput);
  const liveClient = parseMoney(clientPriceInput);
  const { marginPercent, markupPercent } =
    Number.isFinite(liveCost) && Number.isFinite(liveClient)
      ? computePricingMetrics(liveCost, liveClient)
      : { marginPercent: null, markupPercent: null };
  const pct = (v) => (v != null && Number.isFinite(v) ? `${v.toFixed(1)}%` : 'n/a');
  const savedMetrics =
    Number.isFinite(savedCost) && Number.isFinite(savedClient)
      ? computePricingMetrics(savedCost, savedClient)
      : { marginPercent: null, markupPercent: null };

  const accountPath = accountOverviewPath(bid);
  const teamPath = teamQueuePathForBid(bid);
  const logoUrl = isInternalBid(bid) ? INTERNAL_BID_COMPANY.logoUrl : getCrmCompanyLogoDisplayUrl(account);
  const contact = account?.contact || {};
  const contactName = contactDisplayName(account);
  const scopeText = (bid.scope || '').trim() || (bid.teamInput?.scope || '').trim();
  const typeLabel = bid.isPackage ? 'Package' : bid.catalogItemId ? 'Catalog service' : 'Custom service';
  const sourceLabel =
    bid.fromProgramSignup === true ? 'Program signup' : bid.source === 'portal' ? 'Client portal' : 'CRM';

  /** Activity entry for edits that have no timestamp field of their own. */
  const logEntry = (title, detail, tone) => ({ at: new Date(), title, detail, tone, byUid: currentUser?.uid || '' });

  /**
   * One save path for the pricing card. Team members save cost + scope (first save
   * marks the bid "Submitted"); execs can also set the client price (→ Exec review)
   * and publish (→ client portal).
   */
  const handleSave = async ({ publish = false } = {}) => {
    if (!canBid || pricingLocked) return;
    const cost = costInput.trim() === '' ? '' : parseMoney(costInput);
    if (cost !== '' && (!Number.isFinite(cost) || cost < 0)) {
      alert('Enter a valid internal cost.');
      return;
    }
    const client = isExec && clientPriceInput.trim() !== '' ? parseMoney(clientPriceInput) : '';
    if (client !== '' && (!Number.isFinite(client) || client < 0)) {
      alert('Enter a valid client price.');
      return;
    }
    if (publish && client === '') {
      alert('Set the client price before publishing.');
      return;
    }
    if (!isExec && cost === '') {
      alert('Enter your internal cost for this bid.');
      return;
    }
    const scope = scopeInput.trim();

    const payload = { scope, updatedAt: new Date() };
    const log = [];
    const costChanged = cost !== '' && cost !== savedCost;
    const clientChanged = client !== '' && client !== savedClient;

    if (cost !== '') {
      payload.internalCost = cost;
      payload.teamInput = { ...(bid.teamInput || {}), scope, internalCostEstimate: cost };
      if (costChanged) {
        log.push(
          logEntry(
            Number.isFinite(savedCost) ? 'Internal cost updated' : 'Internal cost entered',
            Number.isFinite(savedCost) ? `${formatMoney(savedCost)} → ${formatMoney(cost)}` : formatMoney(cost),
            'blue'
          )
        );
      }
    }
    if (scope !== scopeText && !costChanged && !clientChanged && !publish) {
      log.push(logEntry('Scope of work updated', '', 'gray'));
    }

    if (client !== '') {
      const m = cost !== '' ? computePricingMetrics(cost, client).markupPercent : null;
      payload.clientListPrice = client;
      payload.markupPercent = m != null && Number.isFinite(m) ? Math.round(m * 100) / 100 : '';
      payload.pricingStatus = publish ? PRICING_STATUS.published : PRICING_STATUS.exec_ready;
      if (!bid.teamSubmittedAt && !bid.postedAt) payload.pricedDirectlyByExec = true;
      if (clientChanged) {
        log.push(
          logEntry(
            Number.isFinite(savedClient) ? 'Client price updated' : 'Client price set',
            Number.isFinite(savedClient) ? `${formatMoney(savedClient)} → ${formatMoney(client)}` : formatMoney(client),
            'blue'
          )
        );
      }
      if (publish) {
        payload.publishedAt = new Date();
        payload.publishedByUid = currentUser?.uid || '';
      }
    } else if (
      bid.pricingStatus === PRICING_STATUS.awaiting_team ||
      bid.pricingStatus === PRICING_STATUS.draft
    ) {
      // Team's first price moves the bid from "out" to "priced".
      payload.pricingStatus = PRICING_STATUS.team_submitted;
      payload.teamSubmittedAt = new Date();
    }

    if (log.length > 0) payload.activityLog = arrayUnion(...log);

    setSaving(true);
    try {
      await updateDoc(doc(db, 'alaCarteBids', bid.id), payload);
      await load();
    } catch (e) {
      console.error(e);
      alert(publish ? 'Publish failed' : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteBid(bid);
      navigate('/crm/bids');
    } catch (e) {
      console.error(e);
      alert(e.message || 'Delete failed');
      setSaving(false);
    }
  };

  return (
    <div className="relative min-h-screen p-4 md:p-6 lg:p-8">
      <PortalBackground />
      <div className="relative w-full space-y-6">
        {/* Header — back, title, actions in one row */}
        <header className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
          <div>
            <BackButton to="/crm/bids" />
          </div>
          <div className="min-w-0 text-center">
            <h1 className="truncate text-2xl font-bold tracking-tight text-gray-900 dark:text-white md:text-3xl">
              {bid.icon || '📦'} {bid.name}
            </h1>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-200">{bid.companyName || account?.company || 'Unknown company'}</span>
              <span>·</span>
              <span>{bid.department}</span>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusPillClass(bid.pricingStatus)}`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                {BID_STATUS_LABELS[bid.pricingStatus] || bid.pricingStatus}
              </span>
            </div>
          </div>
          <div className="flex justify-end">
            {isExec &&
              (confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="hidden text-sm text-red-700 dark:text-red-300 sm:inline">Delete permanently?</span>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    disabled={saving}
                    className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:border-red-300 hover:text-red-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Delete bid</span>
                </button>
              ))}
          </div>
        </header>

        {/* Stage tracker */}
        <section className={`${CARD} p-6`}>
          <StageTracker stages={stages} />
        </section>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left: the bid itself */}
          <div className="space-y-6 lg:col-span-2">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <CrmStatCard
                icon={DollarSign}
                label="Client price"
                value={Number.isFinite(savedClient) ? formatMoney(savedClient) : 'Not set'}
                hint={published ? 'Published' : Number.isFinite(savedClient) ? 'Not published yet' : undefined}
                tone={Number.isFinite(savedClient) ? 'green' : 'gray'}
              />
              {canBid && (
                <>
                  <CrmStatCard
                    icon={Building2}
                    label="Internal cost"
                    value={Number.isFinite(savedCost) ? formatMoney(savedCost) : 'Not set'}
                    tone="gray"
                  />
                  <CrmStatCard
                    icon={Percent}
                    label="Margin"
                    value={pct(savedMetrics.marginPercent)}
                    tone={savedMetrics.marginPercent != null && Number.isFinite(savedMetrics.marginPercent) ? 'blue' : 'gray'}
                  />
                  <CrmStatCard
                    icon={TrendingUp}
                    label="Markup"
                    value={pct(savedMetrics.markupPercent)}
                    tone={savedMetrics.markupPercent != null && Number.isFinite(savedMetrics.markupPercent) ? 'purple' : 'gray'}
                  />
                </>
              )}
            </div>

            {/* What's being bid */}
            <section className={`${CARD} p-6`}>
              <h2 className="mb-4 text-center text-lg font-semibold text-gray-900 dark:text-white">
                {bid.isPackage ? 'Package' : 'Service'}
              </h2>
              {bid.description ? (
                <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{bid.description}</p>
              ) : (
                <p className="text-sm text-gray-400">No request notes.</p>
              )}
              {bid.isPackage && bid.packageItems.length > 0 && (
                <ul className="mt-4 divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-gray-800 dark:border-gray-700">
                  {bid.packageItems.map((item, index) => (
                    <li
                      key={`${item.catalogItemId || 'custom'}-${index}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-200"
                    >
                      <span className="text-lg leading-none">{item.icon || '📦'}</span>
                      <span className="min-w-0 flex-1">
                        {item.quantity > 1 ? `${item.quantity} × ` : ''}
                        {item.name}
                        {item.notes ? <span className="text-gray-500 dark:text-gray-400"> — {item.notes}</span> : null}
                      </span>
                      {item.custom && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                          Custom
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {!canBid && scopeText && (
                <div className="mt-5">
                  <h3 className={LABEL}>Scope of work</h3>
                  <p className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{scopeText}</p>
                </div>
              )}
            </section>

            {/* Pricing */}
            {canBid ? (
              <section className={`${CARD} p-6`}>
                <div className="mb-5 flex items-center justify-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pricing</h2>
                  {pricingLocked && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                      Locked — published
                    </span>
                  )}
                </div>
                <div className={`grid gap-4 ${isExec ? 'sm:grid-cols-2 xl:grid-cols-4' : 'sm:grid-cols-2'}`}>
                  <div>
                    <label htmlFor="bid-cost" className={LABEL}>
                      Internal cost
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                      <input
                        id="bid-cost"
                        type="text"
                        inputMode="decimal"
                        value={costInput}
                        disabled={pricingLocked}
                        onChange={(e) => {
                          setCostInput(e.target.value);
                          setConfirmPublish(false);
                        }}
                        placeholder="0.00"
                        className={`${FIELD} pl-7 pr-3`}
                      />
                    </div>
                  </div>
                  {isExec && (
                    <>
                      <div>
                        <label htmlFor="bid-client-price" className={LABEL}>
                          Client price
                        </label>
                        <div className="relative">
                          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                          <input
                            id="bid-client-price"
                            type="text"
                            inputMode="decimal"
                            value={clientPriceInput}
                            disabled={pricingLocked}
                            onChange={(e) => {
                              setClientPriceInput(e.target.value);
                              setConfirmPublish(false);
                            }}
                            placeholder="0.00"
                            className={`${FIELD} pl-7 pr-3`}
                          />
                        </div>
                      </div>
                      <div>
                        <span className={LABEL}>Margin</span>
                        <p className="py-2.5 text-sm font-semibold text-gray-900 dark:text-white">{pct(marginPercent)}</p>
                      </div>
                      <div>
                        <span className={LABEL}>Markup</span>
                        <p className="py-2.5 text-sm font-semibold text-gray-900 dark:text-white">{pct(markupPercent)}</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-4">
                  <label htmlFor="bid-scope" className={LABEL}>
                    Scope of work <span className="normal-case tracking-normal text-gray-400">— shown on the client’s quote</span>
                  </label>
                  <textarea
                    id="bid-scope"
                    rows={5}
                    value={scopeInput}
                    disabled={pricingLocked}
                    onChange={(e) => setScopeInput(e.target.value)}
                    placeholder="Describe everything this bid includes…"
                    className={`${FIELD} px-3`}
                  />
                </div>

                {!pricingLocked &&
                  (confirmPublish ? (
                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50/60 px-4 py-3 dark:border-red-900/60 dark:bg-red-900/10">
                      <p className="text-sm text-red-800 dark:text-red-200">
                        Publish {formatMoney(liveClient)} to {bid.companyName || 'the client'}’s portal? They can see and
                        accept it right away.
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => setConfirmPublish(false)}
                          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => handleSave({ publish: true })}
                          className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          <Send className="h-4 w-4" />
                          {saving ? 'Publishing…' : 'Publish'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleSave()}
                        className={
                          isExec
                            ? 'rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                            : 'rounded-xl bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50'
                        }
                      >
                        {saving
                          ? 'Saving…'
                          : isExec
                            ? 'Save'
                            : bid.teamSubmittedAt
                              ? 'Update bid'
                              : 'Submit bid'}
                      </button>
                      {isExec && (
                        <button
                          type="button"
                          disabled={saving || !Number.isFinite(liveClient) || liveClient < 0}
                          onClick={() => setConfirmPublish(true)}
                          className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          <Send className="h-4 w-4" />
                          Publish to client portal
                        </button>
                      )}
                    </div>
                  ))}
              </section>
            ) : (
              <section className={`${CARD} p-6 text-center text-sm text-gray-500 dark:text-gray-400`}>
                Only the {departmentToPostedTeam(bid.department || 'General')} team or an executive can price this bid.
              </section>
            )}
          </div>

          {/* Right: client, details, activity */}
          <div className="space-y-6">
            <section className={`${CARD} p-5`}>
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                  {logoUrl ? (
                    <img src={logoUrl} alt="" className="max-h-[85%] max-w-[85%] object-contain" />
                  ) : (
                    <Building2 className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-gray-900 dark:text-white">
                    {bid.companyName || account?.company || 'Unknown company'}
                  </p>
                  <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                    {bidAccountTypeLabel(bid.accountType)}
                    {contactName ? ` · ${contactName}` : ''}
                  </p>
                </div>
              </div>
              {(contact.email || contact.phone) && (
                <div className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-red-600">
                      <Mail className="h-4 w-4 text-gray-400" /> {contact.email}
                    </a>
                  )}
                  {contact.phone && (
                    <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-red-600">
                      <Phone className="h-4 w-4 text-gray-400" /> {contact.phone}
                    </a>
                  )}
                </div>
              )}
              {accountPath && (
                <Link
                  to={accountPath}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:underline dark:text-red-400"
                >
                  Open account <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              )}
            </section>

            <section className={`${CARD} p-5`}>
              <h2 className="mb-2 text-center text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Details
              </h2>
              <dl>
                <DetailRow label="Team">{departmentToPostedTeam(bid.department || 'General')}</DetailRow>
                <DetailRow label="Type">{typeLabel}</DetailRow>
                <DetailRow label="Source">{sourceLabel}</DetailRow>
                {bid.quantity > 1 && <DetailRow label="Quantity">{bid.quantity}</DetailRow>}
                <DetailRow label="Created">{formatDate(bid.createdAt) || '—'}</DetailRow>
                {bid.clientResponse?.status && (
                  <DetailRow label="Client response">{CLIENT_RESPONSE_LABELS[bid.clientResponse.status]}</DetailRow>
                )}
                {bid.contract?.status && (
                  <DetailRow label="Contract">{BID_CONTRACT_STATUS_LABELS[bid.contract.status] || bid.contract.status}</DetailRow>
                )}
                {bid.assignedPartner?.companyName && (
                  <DetailRow label="Partner">{bid.assignedPartner.companyName}</DetailRow>
                )}
              </dl>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {bid.contract?.status === BID_CONTRACT_STATUS.completed && bid.contract?.url && (
                  <a
                    href={bid.contract.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 font-medium text-red-600 hover:underline dark:text-red-400"
                  >
                    <FileSignature className="h-4 w-4" /> Signed contract
                  </a>
                )}
                {bid.catalogItemId && (
                  <Link className="font-medium text-red-600 hover:underline dark:text-red-400" to={`/crm/alacartetemplate/${bid.catalogItemId}`}>
                    Catalog template
                  </Link>
                )}
                {teamPath && canBid && (
                  <Link className="font-medium text-red-600 hover:underline dark:text-red-400" to={teamPath}>
                    Team workspace
                  </Link>
                )}
              </div>
            </section>

            <section className={`${CARD} p-5`}>
              <h2 className="mb-4 text-center text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Activity
              </h2>
              {activity.length === 0 ? (
                <p className="text-center text-sm text-gray-400">No activity yet.</p>
              ) : (
                <ol className="relative space-y-4 border-l border-gray-200 pl-5 dark:border-gray-800">
                  {activity.map((event, i) => (
                    <li key={`${event.at.getTime()}-${i}`} className="relative">
                      <span
                        className={`absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-white dark:ring-gray-900 ${TIMELINE_DOT[event.tone] || TIMELINE_DOT.gray}`}
                      />
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{event.title}</p>
                      {event.detail && <p className="text-sm text-gray-600 dark:text-gray-300">{event.detail}</p>}
                      <p className="mt-0.5 text-xs text-gray-400">
                        {formatDateTime(event.at)}
                        {event.who ? ` · ${event.who}` : ''}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
